import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PEAK_DAY_DEMAND_PER_ERC, SET_PEAK_DAY_DEMAND_PER_ERC } from '../../../../../queries/engineeringQrys';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { numbersUpToTwoDecimalPlaces } from '../../../../reuseableComponents/forms/formValidation';
import '../../dwri.css';

const DwriPeakDayDemandPerErc = ({ pwsId, years }) => {

  const [editRow, setEditRow] = React.useState(null);
  const [peakDayVolumeValue, setPeakDayVolumeValue] = React.useState('');
  const [peakDayVolumeError, setPeakDayVolumeError] = React.useState('');
  const [adjustedPeakDayErcValue, setAdjustedPeakDayErcValue] = React.useState('');
  const [adjustedPeakDayErcError, setAdjustedPeakDayErcError] = React.useState('');
  const [adjustmentJustification, setAdjustmentJustification] = React.useState('');
  const [adjustmentJustificationError, setAdjustmentJustificationError] = React.useState('');

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };

  const [setPeakDayDemand] = useMutation(SET_PEAK_DAY_DEMAND_PER_ERC, {
    onCompleted: () => {
      refetch();
    },
    onError: (mutationErr) => {
      console.error('Failed to update adjusted peak day demand per ERC:', mutationErr);
    }
  });

  const { data, loading, error, refetch } = useQuery(GET_PEAK_DAY_DEMAND_PER_ERC, {
      variables: { pwsId, years },
      skip: !pwsId || !years || years.length === 0,
      fetchPolicy: 'cache-and-network',
  });

  if (error) return <p className="text-danger">Error loading Peak Day Demand per ERC</p>;
  if (loading) return <p>Loading...</p>;

  function handleEditClick(row) {
    setEditRow(row);
    setPeakDayVolumeValue(row.peakDayVolume == null ? '' : String(row.peakDayVolume));
    setPeakDayVolumeError('');
    setAdjustedPeakDayErcValue(row.adjustedPeakDayErc == null ? '' : String(row.adjustedPeakDayErc));
    setAdjustedPeakDayErcError('');
    setAdjustmentJustification(row.adjustmentJustification ?? '');
    setAdjustmentJustificationError('');
  }

  function handleModalClose() {
    setEditRow(null);
    setPeakDayVolumeValue('');
    setPeakDayVolumeError('');
    setAdjustedPeakDayErcValue('');
    setAdjustedPeakDayErcError('');
    setAdjustmentJustification('');
    setAdjustmentJustificationError('');
  }

  function handlePeakDayVolumeChange(event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    if (validationResult === true) {
      setPeakDayVolumeValue(nextValue);
      setPeakDayVolumeError('');
    } else {
      setPeakDayVolumeError(validationResult);
    }
  }

  function handlePeakDayVolumeBlur() {
    const validationResult = numbersUpToTwoDecimalPlaces(peakDayVolumeValue);
    setPeakDayVolumeError(validationResult === true ? '' : validationResult);
  }

  function handleAdjustedPeakDayErcChange(event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    if (validationResult === true) {
      setAdjustedPeakDayErcValue(nextValue);
      setAdjustedPeakDayErcError('');
    } else {
      setAdjustedPeakDayErcError(validationResult);
    }
  }

  function handleAdjustedPeakDayErcBlur() {
    const validationResult = numbersUpToTwoDecimalPlaces(adjustedPeakDayErcValue);
    setAdjustedPeakDayErcError(validationResult === true ? '' : validationResult);
  }

  function isPeakDayErcChanged() {
    if (!editRow) {
      return false;
    }
    return editRow.adjustedPeakDayErc !== (adjustedPeakDayErcValue === '' ? null : Number(adjustedPeakDayErcValue));
  }

  async function handleSave() {
    let validationError = false;

    if (!peakDayVolumeValue.trim()) {
      setPeakDayVolumeError('Peak Day Volume is required.');
      validationError = true;
    } else {
      const volumeResult = numbersUpToTwoDecimalPlaces(peakDayVolumeValue);
      if (volumeResult !== true) {
        setPeakDayVolumeError(volumeResult);
        validationError = true;
      }
    }

    const ercResult = numbersUpToTwoDecimalPlaces(adjustedPeakDayErcValue);
    if (adjustedPeakDayErcValue !== '' && ercResult !== true) {
      setAdjustedPeakDayErcError(ercResult);
      validationError = true;
    }

    if (adjustedPeakDayErcValue !== '' && !adjustmentJustification.trim()) {
      setAdjustmentJustificationError('Justification is required when setting an Adjusted Peak Day ERC.');
      validationError = true;
    }

    if (validationError) {
      return;
    }
    try {
      await setPeakDayDemand({
        variables: {
          updatePeakDayDemandInput: {
            pwsId,
            year: editRow.year,
            peakDayGallonsPerDay: peakDayVolumeValue === '' ? null : Number(peakDayVolumeValue),
            adjustedPeakDayErc: adjustedPeakDayErcValue === '' ? null : Number(adjustedPeakDayErcValue),
            adjustmentJustification,
          },
        },
      });
      handleModalClose();
    } catch (mutationErr) {
      console.error('Failed to update adjusted peak day demand per ERC:', mutationErr);
    }
  }

  const rows = (data?.getPeakDayDemandPerErc || []).map((row) => ({
    key: `${row.deqId}-${row.year}`,
    year: row.year,
    peakDay: row.peakDate,
    peakDayVolume: row.peakDayGallonsPerDay,
    peakDayWholesaleVolume: row.peakDayWholesaleGallonsPerDay,
    peakDayErc: row.peakDayErc,
    adjustedPeakDayErc: row.adjustedPeakDayErc,
    adjustmentJustification: row.adjustmentJustification,
  }));


  return (
    <>
      <Row>
        <Col><span><b>Peak Day Demand per ERC (Gal/Day)</b></span></Col>
      </Row>
      <div className="table-responsive">
        <Row className="dwri-service-connections-header gx-0">
          <Col className="dwri-table-header-cell dwri-col-year"><span><b>Year</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day Volume</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day Wholesale Volume</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Peak Day ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Adjusted Peak Day ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Adjustment Justification</b></span></Col>
          <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
        </Row>
        {rows.map((row, idx) => (
          <Row key={row.key} className={`dwri-service-connections-row gx-0 ${idx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
            <Col className="dwri-table-cell dwri-col-year"><span>{row.year}</span></Col>
            <Col className="dwri-table-cell"><span>{row.peakDay}</span></Col>
            <Col className="dwri-table-cell"><span>{row.peakDayVolume}</span></Col>
            <Col className="dwri-table-cell"><span>{row.peakDayWholesaleVolume}</span></Col>
            <Col className="dwri-table-cell"><span>{row.peakDayErc}</span></Col>
            <Col className="dwri-table-cell"><span>{row.adjustedPeakDayErc}</span></Col>
            <Col className="dwri-table-cell"><span>{row.adjustmentJustification}</span></Col>
            {user?.permissions?.includes('edit_water_use_system') && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit service connections for year ${row.year}`}
                type="button"
                onClick={() => handleEditClick(row)}
              >
                Edit
              </button>
            </Col>
            )}
          </Row>
        ))}
      </div>
      {editRow && (
      <Modal
        onClose={handleModalClose}
        onEscape={handleModalClose}
      >
        <ModalTitle>Update Peak Day Demand per ERC (gal/day)</ModalTitle>
        <ModalContent>
          <div className="dwri-peak-day-demand-modal-grid">
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
              <TextInput label="Year" value={String(editRow.year ?? '')} isDisabled />
              <TextInput label="Peak Day" value={String(editRow.peakDay ?? '')} isDisabled />
            </div>
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
              <TextInput
                label="Peak Day Volume"
                value={peakDayVolumeValue}
                onChange={handlePeakDayVolumeChange}
                onBlur={handlePeakDayVolumeBlur}
                errorMessage={peakDayVolumeError || undefined}
              />
              <TextInput label="Peak Day Wholesale Volume" value={String(editRow.peakDayWholesaleVolume ?? '')} isDisabled />
            </div>
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
              <TextInput label="Peak Day ERC" value={String(editRow.peakDayErc ?? '')} isDisabled />
              <TextInput
                label="Adjusted Peak Day ERC"
                value={adjustedPeakDayErcValue}
                onChange={handleAdjustedPeakDayErcChange}
                onBlur={handleAdjustedPeakDayErcBlur}
                errorMessage={adjustedPeakDayErcError || undefined}
              />
            </div>
            <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--full">
              <TextArea
                label="Peak Day Demand Change Justification"
                value={adjustmentJustification}
                onChange={(event) => {
                  setAdjustmentJustification(event.target.value);
                  setAdjustmentJustificationError('');
                }}
                errorMessage={adjustmentJustificationError || undefined}
                isRequired={isPeakDayErcChanged()}
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="dwri-service-modal-footer">
          <button type="button" onClick={handleModalClose}>Close</button>
          <button type="button" onClick={handleSave}>Save</button>
        </ModalFooter>
      </Modal>
    )}
    </>
  );
}

export default DwriPeakDayDemandPerErc;